import T from "@/components/forms/TextField.module.css";

import { useAriaField } from "@/components/forms/ariaFieldRegister.ts";
import { WFakeForm } from "@/components/forms/WHookForm.tsx";
import { siteThemeAtom } from "@/config.ts";
import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { PaymentResult, type PaymentParams, type PaymentProps } from "@/modules/billing/payment/PaymentParams.ts";
import {
    getRedirectUrlFromData,
    linkSendData,
    persistentDataRequest,
    redirectToGatewayAndWaitForResponse,
} from "@/modules/billing/payment/paymentUtils.ts";
import { DescriptionLayout, ErrorText } from "@/modules/billing/payment_forms/components.tsx";
import { t_StartingPayment } from "@/modules/billing/progressLabels.tsx";
import { parseSslcommerzPersistentData } from "@/modules/billing/sslcommerz/parseSslcommerzPersistentData.ts";
import { isTestEnvironment } from "@/utils/environment.ts";
import { useUnmountSignal } from "@/utils/react/useUnmountSignal.tsx";
import { Trans, useLingui } from "@lingui/react/macro";
import type {
    MethodConfig,
    ProcessorConfig,
    SslcommerzRedirectSendDataResponse,
} from "@warrenio/api-spec/spec.oats.gen";
import { useAtomValue } from "jotai";
import { useImperativeHandle, useRef, type Ref } from "react";
import { Input, TextField } from "react-aria-components";
import { useForm } from "react-hook-form";

export default function SslcommerzPaymentForm({
    actionsRef,
    isSubmitting,
    method: { key },
}: {
    config: ProcessorConfig & { type: "link" };
    method: MethodConfig;
} & PaymentProps) {
    //#region Hooks

    const theme = useAtomValue(siteThemeAtom);
    const apiClient = useApiClient();
    const signal = useUnmountSignal();
    const getExtraRef = useRef<GetExtraSourceFields>(null);

    useImperativeHandle(actionsRef, () => ({
        needsPopUp(_params) {
            return true;
        },

        async makePayment(params: PaymentParams) {
            const { progress, popup, account } = params;

            // Query the form component for extra fields
            const extraFields = await getExtraRef.current?.();
            if (extraFields === false) {
                return PaymentResult.VALIDATION_FAILED;
            }

            progress(t_StartingPayment);
            const { data } = await linkSendData<SslcommerzRedirectSendDataResponse>(apiClient, params, "sslcommerz", {
                paymentMethod: key,
                ...extraFields,
            });

            const { persistent_data_ref } = data;

            return await redirectToGatewayAndWaitForResponse({
                popup,
                progress,
                redirectUrl: getRedirectUrlFromData(data),
                signal,
                check: async () =>
                    parseSslcommerzPersistentData(
                        await persistentDataRequest(apiClient, account.id, persistent_data_ref, signal),
                    ) != null,
            });
        },
    }));
    //#endregion

    return (
        <>
            <SslcommerzForm isSubmitting={isSubmitting} getExtraRef={getExtraRef} />
            {(theme === "tenbyte" || isTestEnvironment) && <TenbyteTermsAndConditions />}
        </>
    );
}

function TenbyteTermsAndConditions() {
    return (
        <p>
            <Trans>
                By continuing with the top up you agree to the{" "}
                <a
                    className="react-aria-Link"
                    target="_blank"
                    href="https://tenbytecloud.com/terms-and-conditions/"
                    rel="noopener noreferrer"
                >
                    Terms & Conditions
                </a>
                ,{" "}
                <a
                    className="react-aria-Link"
                    target="_blank"
                    href="https://tenbytecloud.com/privacy-policy/"
                    rel="noopener noreferrer"
                >
                    Privacy Policy
                </a>{" "}
                &{" "}
                <a
                    className="react-aria-Link"
                    target="_blank"
                    href="https://tenbytecloud.com/refund-policy-bd/"
                    rel="noopener noreferrer"
                >
                    Refund Policy
                </a>
                .
            </Trans>
        </p>
    );
}

interface ExtraSourceParams {
    phone_number: string;
}

type GetExtraSourceFields = () => Promise<ExtraSourceParams | false>;

interface FormProps {
    isSubmitting: boolean;
    getExtraRef: Ref<GetExtraSourceFields>;
}

function SslcommerzForm({ getExtraRef, isSubmitting }: FormProps) {
    const { t } = useLingui();

    interface SslcommerzInputs {
        phone_number: ExtraSourceParams["phone_number"];
    }

    //#region Hooks
    const form = useForm<SslcommerzInputs>({
        disabled: isSubmitting,
        defaultValues: {
            phone_number: "",
        },
    });

    useImperativeHandle(getExtraRef, () => async () => {
        const valid = await form.trigger(undefined, { shouldFocus: true });
        if (!valid) {
            console.debug("Form validation failed");
            return false;
        }

        return form.getValues();
    });

    const { ref, props } = useAriaField("phone_number", form, { isRequired: true });
    //#endregion

    return (
        <WFakeForm form={form}>
            <TextField className={T.TextField} {...props}>
                <DescriptionLayout
                    label={t`Phone number`}
                    description={t`The phone/mobile number to contact if any issue arises`}
                >
                    <Input className={T.Input} ref={ref} autoFocus={true} />
                    <ErrorText text={props.errorMessage} />
                </DescriptionLayout>
            </TextField>
        </WFakeForm>
    );
}
